
/**
 * Convert grade range [low, high] to maturity band marks [1-4].
 * Handles exact single-band matches and mixed ranges.
 * Boundary values (3, 6, 9) use context-aware logic based on range endpoints.
 * @param {number} low
 * @param {number} high
 * @returns {number[]} [lowMark, highMark] where marks are 1-4 (LE, UE, MS, HS)
 */
function toBand (low, high) {
  const exact = {
    '0,3': [1, 1], // LE
    '3,6': [2, 2], // UE
    '6,8': [3, 3], // MS
    '9,12': [4, 4] // HS
  };

  if (exact[`${low},${high}`]) {
    return exact[`${low},${high}`];
  }

  // Boundary points: when low=high at a band start, use that band
  if (low === high && (low === 3 || low === 6 || low === 9)) {
    const lowBand = (val) => {
      if (val < 3) return 1;
      if (val < 6) return 2;
      if (val < 9) return 3;
      return 4;
    };
    return [lowBand(low), lowBand(high)];
  }

  // For mixed ranges, map endpoints with context awareness
  const lowBand = (val) => {
    if (val < 3) return 1; // LE starts at 0
    if (val < 6) return 2; // UE starts at 3
    if (val < 9) return 3; // MS starts at 6
    return 4; // HS starts at 9
  };

  const highBand = (val) => {
    if (val <= 3) return 1; // LE ends at 3
    if (val <= 6) return 2; // UE ends at 6
    if (val <= 8) return 3; // MS ends at 8
    return 4; // HS ends at 12
  };

  return [lowBand(low), highBand(high)];
}

/**
 * Transform the low value from maturity range slider grade bands
 * into numerical grades.
 * @param {number} lowSliderMark with possible values in [1, 2, 3, 4]
 * @returns {number} returns the numerical grade of the high end of the band
 * @example
 * given 1 as the first mark for LE (Lower Elementary), which is defined as grades 0 - 3
 * since 0 is the low end:
 * lowSliderMark(1) -> 0
 */
function fromBandLow (lowSliderMark) {
  switch (lowSliderMark) {
    case 1: return 0;
    case 2: return 3;
    case 3: return 6;
    case 4: return 9;
    default:
      throw new Error(`Invalid maturity range, low slider mark ${lowSliderMark}`);
  }
}

/**
 * Transform the high value from maturity range slider grade bands
 * into numerical grades.
 * @param {number} highSliderMark with possible values in [1, 2, 3, 4]
 * @returns {number} returns the numerical grade of the high end of the band
 * @example
 * Given 2 as the second mark for UE (Upper Elementary), which is defined as grades 3 - 6
 * since 6 is the high end:
 * highSliderMark(2) -> 6
 */
function fromBandHigh (highSliderMark) {
  switch (highSliderMark) {
    case 1: return 3;
    case 2: return 6;
    case 3: return 8;
    case 4: return 12;
    default:
      throw new Error(`Invalid maturity range, high slider mark ${highSliderMark}`);
  }
}

/**
 * Convert grade range [low, high] to maturity band marks [1-4].
 * Delegates to toBand which handles exact matches and mixed ranges.
 * @param {number[] | null | undefined} value [low, high] grade numbers from DB
 * @returns {number[]} [lowMark, highMark] where marks are 1-4 (LE, UE, MS, HS)
 */
function formatMaturityRange (value) {
  const [low, high] = value || [4, 12];
  return toBand(low, high);
}

/**
 * Transform values for our maturity range slider. This converts from the
 * grade bands in the slider to actual grade numbers we can send to the Monolith.
 * @param {array} value
 * @returns {array}
 */
function transformMaturityRange (value) {
  const [low, high] = value;

  return [
    fromBandLow(low),
    fromBandHigh(high)
  ];
}

export const maturityRangeField = {
  input: 'slider',
  name: 'maturityRange',
  isRange: true,
  minValue: 1,
  maxValue: 4,
  hasSteps: false, // Only allow selecting a discrete mark.
  marks: {
    1: 'LE',
    2: 'UE',
    3: 'MS',
    4: 'HS'
  },
  // Transform the values before displaying them in the slider.
  format: formatMaturityRange,
  // Transform the values before saving them.
  transform: transformMaturityRange
};
